import React, {useCallback, useMemo} from 'react'
import {Checkbox} from '@/components/ui/checkbox'
import slugify from 'slugify'

interface FacetOption {
    value: string
    display_name: string
    count: number
}

interface Facet {
    name: string
    options: FacetOption[]
}

interface SelectedFacetItem {
    value: string
    display_name: string
}

interface SelectedFacets {
    [key: string]: SelectedFacetItem[]
}

interface ShowMoreState {
    [key: string]: boolean
}

interface FacetOptionsSectionProps {
    facet: Facet
    showMore: ShowMoreState
    onShowMoreToggle: (facetName: string) => void
    selectedCioFacets: SelectedFacets
    onCheckboxChange: (facetName: string, option: FacetOption) => void
    dataTestId?: string
}

export const FacetOptionsSection = ({
    facet,
    showMore,
    onShowMoreToggle,
    selectedCioFacets,
    onCheckboxChange,
    dataTestId
}: FacetOptionsSectionProps) => {
    // Memoize the selected values to ensure stable comparison
    const selectedValues = useMemo(() => {
        return new Set(
            selectedCioFacets[facet.name]?.map((item: SelectedFacetItem) => item.value) || []
        )
    }, [selectedCioFacets, facet.name])

    const handleCheckboxChange = useCallback(
        (facetName: string, option: FacetOption, checked: boolean) => {
            const isCurrentlySelected = selectedValues.has(option.value)

            // Only trigger the change if the new state differs from current state
            if (checked !== isCurrentlySelected) {
                onCheckboxChange(facetName, option)
            }
        },
        [selectedValues, onCheckboxChange]
    )

    return (
        <div className="space-y-2 mt-2">
            {facet.options
                .slice(0, showMore[facet.name] ? facet.options.length : 5)
                .map((option: FacetOption) => {
                    const isChecked = selectedValues.has(option.value)
                    const safeId = slugify(`${facet.name}-${option.value}`)

                    return (
                        <div key={safeId} className="flex items-center gap-2">
                            <Checkbox
                                id={safeId}
                                checked={isChecked}
                                onCheckedChange={(checked) => {
                                    handleCheckboxChange(facet.name, option, !!checked)
                                }}
                                data-testid={dataTestId || 'CioFiltersDrawerContent-Checkbox'}
                            />
                            <label htmlFor={safeId} className="text-sm">
                                {option.display_name}
                                <span className="text-xs text-gray-500 pl-2">({option.count})</span>
                            </label>
                        </div>
                    )
                })}
            {facet.options.length > 5 && (
                <button
                    className="text-blue-500 text-sm"
                    onClick={() => onShowMoreToggle(facet.name)}
                >
                    {showMore[facet.name] ? 'Show less' : 'Show more'}
                </button>
            )}
        </div>
    )
}
